import { dailySpinChainId } from '@/constants/shared';
import { RAFFLE_ABI } from '@/network/abi/raffle';
import { useCallback, useMemo } from 'react';
import { useReadContract, useReadContracts } from 'wagmi';
import { useAuth } from './useAuth';
import { useDailySpinOffchain } from './useDailySpinOffchain';

export type RafflePrize = PrizeDetails & {
  yourTickets: number | null;
  isWon: boolean;
  id: number;
  endsAt: Date | undefined;
  hasEnded: boolean;
  formId: string;
  claimed: boolean;
};

export type PrizeDetails = {
  name: string;
  description: string;
  value: number;
  totalTickets: number;
  isActive: boolean;
  endsAt?: Date;
  totalUsers: number;
  endTimestamp?: number;
  formId: string;
  winners:
    | {
        winnerAddress: `0x${string}`;
        winningTicketIndex: number;
        drawnAt: number;
        claimed: boolean;
      }[]
    | undefined;
};

export const useRaffle = () => {
  const { address, isAuthenticated } = useAuth();
  const { raffleUserPrizeData } = useDailySpinOffchain();
  // Fetch all prizes
  const {
    data: prizesDataWithoutWinners,
    isLoading: isLoadingPrizes,
    refetch: refetchPrizes,
    error: prizesError,
  } = useReadContract({
    address: import.meta.env.VITE_RAFFLE_CONTRACT_ADDRESS,
    abi: RAFFLE_ABI,
    chainId: dailySpinChainId,
    functionName: 'getPrizeDetails',
    query: {
      refetchOnMount: false,
    },
  });

  const prizeWinnerCalls = useMemo(() => {
    if (!prizesDataWithoutWinners) return [];
    return prizesDataWithoutWinners.map((_, index) => ({
      address: import.meta.env.VITE_RAFFLE_CONTRACT_ADDRESS,
      abi: RAFFLE_ABI,
      chainId: dailySpinChainId,
      functionName: 'getPrizeWinners' as const,
      args: [index + 1] as const, // prizeId = index + 1,
      query: {
        refetchOnMount: false,
      },
    }));
  }, [prizesDataWithoutWinners]);

  const {
    data: winnersData,
    isLoading: isLoadingWinners,
    refetch: refetchWinners,
    error: winnersError,
  } = useReadContracts({
    contracts: prizeWinnerCalls,
    query: { enabled: prizeWinnerCalls.length > 0 },
  });
  const refetchRaffle = useCallback(() => {
    refetchPrizes?.();
    refetchWinners?.();
  }, [refetchPrizes, refetchWinners]);
  const prizesDataWithWinners = useMemo(() => {
    if (!prizesDataWithoutWinners || !winnersData) return [];
    return prizesDataWithoutWinners.map((datumWithoutWinner, idx) => {
      return {
        ...datumWithoutWinner,
        value: Number(datumWithoutWinner.value),
        totalTickets: Number(datumWithoutWinner.totalTickets),
        totalUsers: Number(datumWithoutWinner.totalUsers),
        endTimestamp: Number(datumWithoutWinner.endTimestamp),
        winners: winnersData[idx].result?.map((w) => ({
          ...w,
          winningTicketIndex: Number(w.winningTicketIndex),
          drawnAt: Number(w.drawnAt),
        })),
      };
    });
  }, [prizesDataWithoutWinners, winnersData]);

  const rafflePrizes: RafflePrize[] = useMemo(() => {
    if (!prizesDataWithWinners) return [] as RafflePrize[];

    return prizesDataWithWinners.map((prize, index) => {
      const prizeId = index + 1; // 1 based index onchain for Raffle prizes
      const endsAt = prize.endTimestamp && prize.endTimestamp > 0 ? new Date(prize.endTimestamp * 1000) : undefined;

      const userTickets = raffleUserPrizeData?.find((p) => p.prizeId === prizeId)?.ticketsEntered ?? 0;
      const winnerIndex = prize.winners?.findIndex((winner) => winner?.winnerAddress === address);
      const claimedIndex = prize.winners?.findIndex((winner) => winner?.winnerAddress === address && winner?.claimed);
      return {
        ...prize,
        claimed: typeof claimedIndex === 'number' && claimedIndex !== -1,
        id: prizeId,
        // If user is not authenticated, return prizes without user-specific data
        yourTickets: isAuthenticated ? userTickets : null,
        isWon: typeof winnerIndex === 'number' && winnerIndex !== -1,
        endsAt,
        hasEnded: endsAt ? endsAt.getTime() < Date.now() : false,
      };
    });
  }, [prizesDataWithWinners, isAuthenticated, raffleUserPrizeData, address]);

  const unclaimedRaffleWin = rafflePrizes.find((p) => {
    if (p.claimed) return false;
    if (!p.isWon) return false;
    if (p.isActive) return false;
    const ended = p.endsAt && p.endsAt.getTime?.() < Date.now();
    if (!ended) return false;
    return true;
  });

  return {
    rafflePrizes,
    unclaimedRaffleWin,
    isLoadingPrizes,
    isLoadingWinners,
    isLoadingRaffleData: isLoadingPrizes || isLoadingWinners,
    winnersData,
    winnersError,
    prizesError,
    refetchRaffle,
  };
};
